import type { HttpClient } from '@wix/cli-http-client';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import pWaitFor from 'p-wait-for';
import { variant, fields } from 'variant';
import type { VariantOf } from 'variant';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import {
  getDeployment,
  publishDeployment,
  getDeploymentPipelineDescription,
} from '@wix/ambassador-document-management-editor-deployments-v1-deployment/http';
import { DeploymentPipelinesStatus } from '@wix/ambassador-document-management-editor-deployments-v1-deployment/types';
import {
  getDeploymentSchema,
  getDeploymentPipelineDescriptionSchema,
} from './schemas.js';

const { minRetryTimeout, pollInterval } = getTestOverrides();

const VELO_PIPELINE_ID = '5c696513-2584-4f19-ae0f-a559dd649482';

export const DeploymentStatus = variant({
  Success: {},
  Error: fields<{ deploymentError: CliError }>(),
});
export type DeploymentStatus = VariantOf<typeof DeploymentStatus>;

export class DocumentManagementClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    this.retryOptions = {
      retries: 1,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  getDeploymentDetails = async (deploymentId: string) => {
    const deploymentStatus = await this.getDeploymentStatus(deploymentId);

    if (deploymentStatus === DeploymentPipelinesStatus.ERROR) {
      const tasksDescriptions =
        await this.getDeploymentTasksDescriptions(deploymentId);

      return DeploymentStatus.Error({
        deploymentError: new CliError({
          code: CliErrorCode.DeploymentPipelineFailed({ tasksDescriptions }),
          cause: null,
        }),
      });
    }

    return DeploymentStatus.Success();
  };

  private getDeploymentStatus = async (deploymentId: string) => {
    try {
      return await pWaitFor(
        async () => {
          const { data } = await pRetry(
            () => this.httpClient.request(getDeployment({ deploymentId })),
            this.retryOptions
          );

          const result = getDeploymentSchema.safeParse(data);

          if (!result.success) {
            throw new CliError({
              code: CliErrorCode.InvalidGetDeploymentResponse(),
              cause: result.error,
              info: { data },
            });
          }

          const { deploymentPipelinesStatus: status } = result.data.deployment;

          // Continue polling
          if (status === DeploymentPipelinesStatus.IN_PROGRESS) {
            return false;
          }

          return pWaitFor.resolveWith(status);
        },
        {
          // Poll each 2 seconds
          interval: pollInterval ?? 2 * 1000,
          // Fail if 2 minutes passed
          timeout: 2 * 60 * 1000,
        }
      );
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.FailedToGetDeploymentStatus(),
        cause: error,
      });
    }
  };

  private getDeploymentTasksDescriptions = async (deploymentId: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            getDeploymentPipelineDescription({
              deploymentId,
              deploymentPipelinesIds: [VELO_PIPELINE_ID],
            })
          ),
        this.retryOptions
      );

      const result = getDeploymentPipelineDescriptionSchema.safeParse(data);

      if (!result.success) {
        throw new CliError({
          code: CliErrorCode.InvalidGetDeploymentPipelineDescriptionResponse(),
          cause: result.error,
          info: { data },
        });
      }

      const veloPipeline = result.data.pipelinesDescription.find(
        (item) => item.deploymentPipelineId === VELO_PIPELINE_ID
      );

      return veloPipeline?.tasksDescriptions ?? [];
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetDeploymentPipelineDescription(),
        cause: e,
      });
    }
  };

  publishDeployment = async (deploymentId: string) => {
    try {
      await this.httpClient.request(
        publishDeployment({ deploymentId, skipPipelineCheck: true })
      );
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.FailedToPublishDeployment(),
        cause: error,
      });
    }
  };
}
